import './style.scss';

import { useEffect, useMemo, useRef, useState } from 'react';

import { usePersistentState } from '../../../../hooks/usePersistentState';
import { type IResultAnalyticsData, type IResultAnalyticsDataResponse } from '../../../../interface/state/IElectionState';
import { getAssignmentsByEntity } from '../../../../lib/agent-assignment-api';
import { HvcLoad, useCopy } from '../../../utils/hooks';
import { AnalyticsHeader, EmptyState, ErrorState, PuList, PuToolbar } from './components';
import {
	applyToolbar,
	COPY_ALL_SENTINEL,
	deriveCoverage,
	derivedStats,
	type RowCoverage,
	type SortMode,
	type StatusFilter,
	type ViewMode,
} from './utils';
import ViewModeSwitch from './ViewModeSwitch';
import KanbanView from './views/KanbanView';
import TableView from './views/TableView';
import TreeView from './views/TreeView';

interface IAnalyticsLevelViewProps {
	data?: IResultAnalyticsDataResponse;
	load: boolean;
	error: boolean;
	entityLabel: string;
	electionId?: number;
	// Open the read-only Agent Coverage drawer for a row.
	onOpenCoverage: (item: IResultAnalyticsData) => void;
	onPage: (page: number) => void;
	onRetry: () => void;
	ariaLabel?: string;
	// The entity whose children this list shows — used as the Tree view's root node.
	rootLabel?: string;
	rootLevelLabel?: string;
	/** Unread messages by assigned-agent id — only the overview has conversations with these agents. */
	unreadByAgentId?: ReadonlyMap<number, number>;
}

const TOOLBAR_THRESHOLD = 6;

/**
 * Self-contained analytics level: the `.quick-analytics` card with header (ring +
 * Expected/Uploaded/Remaining), a view-mode switch, the search/filter/sort toolbar,
 * and the child rows rendered as Card / Table / Kanban / Tree. Data source is
 * injected — the Overview card feeds it from Redux (`useResultAnalytics`); the Agent
 * Coverage drawer's children tab feeds it from the isolated scoped client.
 */
const AnalyticsLevelView = ({
	data,
	load,
	error,
	entityLabel,
	electionId,
	onOpenCoverage,
	onPage,
	onRetry,
	ariaLabel = 'Quick Result Analytics',
	rootLabel,
	rootLevelLabel,
	unreadByAgentId,
}: IAnalyticsLevelViewProps) => {
	const [search, setSearch] = useState('');
	const [statusFilter, setStatusFilter] = useState<StatusFilter>('all');
	const [sort, setSort] = useState<SortMode>('uploadedFirst');
	const [viewMode, setViewMode] = usePersistentState<ViewMode>('ielect-childlist-viewmode', 'card');

	const copyProps = useCopy();

	const rows = useMemo(() => data?.data ?? [], [data]);
	const filtered = useMemo(() => applyToolbar(rows, { search, statusFilter, sort }), [rows, search, statusFilter, sort]);
	const stats = useMemo(() => derivedStats(data?.statistics, rows.length), [data, rows.length]);

	// Inline coverage: batch-fetch the assigned agent for the visible page's codes
	// (one call) and key it by entityCode. A failure here is isolated — badges fall
	// back to neutral and never affect the analytics widget or the session.
	const [coverage, setCoverage] = useState<Map<string, RowCoverage>>(new Map());
	const [coverageState, setCoverageState] = useState<'idle' | 'loading' | 'error'>('idle');
	const coverageReqRef = useRef(0);
	const pageCodesKey = useMemo(
		() =>
			rows
				.map((r) => r.code)
				.sort()
				.join('|'),
		[rows]
	);

	useEffect(() => {
		const codes = rows.map((r) => r.code).filter(Boolean);
		if (!electionId || codes.length === 0) {
			setCoverage(new Map());
			setCoverageState('idle');
			return;
		}
		const reqId = coverageReqRef.current + 1;
		coverageReqRef.current = reqId;
		setCoverageState('loading');
		getAssignmentsByEntity(codes, electionId).then((outcome) => {
			if (reqId !== coverageReqRef.current) return; // a newer page superseded this request
			if (!outcome.reachable || outcome.httpStatus < 200 || outcome.httpStatus >= 300) {
				setCoverageState('error');
				return;
			}
			const map = new Map<string, RowCoverage>();
			(outcome.body?.data?.items ?? []).forEach((item) => map.set(item.entityCode, deriveCoverage(item)));
			setCoverage(map);
			setCoverageState('idle');
		});
	}, [pageCodesKey, electionId]);

	const hasData = rows.length > 0;
	const hasActiveFilter = !!search || statusFilter !== 'all';
	const coverageLoading = coverageState === 'loading';

	const handleCopy = (value: string, key: string) => {
		copyProps.setAction(key);
		copyProps.copy(value);
	};

	const handleCopyAll = () => handleCopy(rows.map((r) => r.code).join('\n'), COPY_ALL_SENTINEL);

	const currentPage = data?.currentPage ?? 1;
	const totalPages = data?.totalPages ?? 1;

	const renderList = () => {
		if (viewMode === 'table')
			return (
				<TableView
					rows={filtered}
					copiedCode={copyProps.action}
					copySuccess={copyProps.copySuccess}
					onCopy={handleCopy}
					onSelectChild={onOpenCoverage}
					coverage={coverage}
					coverageLoading={coverageLoading}
					currentPage={currentPage}
					totalPages={totalPages}
					onPage={onPage}
					hasActiveFilter={hasActiveFilter}
					entityLabel={entityLabel}
				/>
			);
		if (viewMode === 'kanban')
			return (
				<KanbanView
					rows={filtered}
					onSelectChild={onOpenCoverage}
					coverage={coverage}
					coverageLoading={coverageLoading}
					currentPage={currentPage}
					totalPages={totalPages}
					onPage={onPage}
				/>
			);
		if (viewMode === 'tree')
			return (
				<TreeView
					rows={filtered}
					coverage={coverage}
					coverageLoading={coverageLoading}
					onSelectChild={onOpenCoverage}
					electionId={electionId}
					entityLabel={entityLabel}
					rootLabel={rootLabel}
					rootLevelLabel={rootLevelLabel}
					hasActiveFilter={hasActiveFilter}
					currentPage={currentPage}
					totalPages={totalPages}
					onPage={onPage}
				/>
			);
		return (
			<PuList
				rows={filtered}
				copiedCode={copyProps.action}
				copySuccess={copyProps.copySuccess}
				onCopy={handleCopy}
				onSelectChild={onOpenCoverage}
				coverage={coverage}
				coverageLoading={coverageLoading}
				currentPage={currentPage}
				totalPages={totalPages}
				onPage={onPage}
				hasActiveFilter={hasActiveFilter}
				unreadByAgentId={unreadByAgentId}
			/>
		);
	};

	// The search/filter/sort toolbar only helps beyond a handful of rows; the view
	// switch is useful at any size, so it lives in its own always-visible bar.
	const showToolbar = stats.total > TOOLBAR_THRESHOLD && viewMode !== 'tree';

	return (
		<section
			className='quick-analytics'
			aria-label={ariaLabel}
		>
			<HvcLoad
				load={load}
				view
				loadTxt='Loading result analytics…'
			>
				{error && !hasData ? (
					<ErrorState onRetry={onRetry} />
				) : !hasData ? (
					<EmptyState entityLabel={entityLabel} />
				) : (
					<>
						<AnalyticsHeader
							stats={stats}
							entityLabel={entityLabel}
							onCopyAll={handleCopyAll}
							copiedAll={copyProps.copySuccess && copyProps.action === COPY_ALL_SENTINEL}
							hasCodes={hasData}
						/>

						<div className='qa-viewbar'>
							<ViewModeSwitch
								value={viewMode}
								onChange={setViewMode}
							/>
						</div>

						{showToolbar && (
							<PuToolbar
								search={search}
								onSearch={setSearch}
								statusFilter={statusFilter}
								onStatusFilter={setStatusFilter}
								sort={sort}
								onSort={setSort}
							/>
						)}

						{renderList()}
					</>
				)}
			</HvcLoad>
		</section>
	);
};

export default AnalyticsLevelView;
